/**
 * In-memory flag definitions for OpenFeature's LocalProvider.
 * This file contains the flag definitions for the LocalProvider.
 * It is used to define the flags that are available in the LocalProvider.
 * The flags are defined as an object with the flag key as the key and the flag definition as the value.
 * The flag definition is an object with the following properties:
 * - type: The type of the flag. This can be one of 'boolean', 'string', 'number', or 'object'.
 * - defaultValue: The default value of the flag. This is the value that will be returned if the flag is not found in the LocalProvider.
 * - description: A description of the flag. This is used to provide information about the flag to the user.
 * - values: An array of possible values for the flag. This is used to provide a list of possible values for the flag to the user.
 */

interface EvenSplitDefinition<T> {
  weightedSplit?: false
  values: T[]
}

interface WeightedSplitDefinition<T> {
  weightedSplit: true
  values: {
    value: T
    weight: number
  }[]
}

export type FeatureDefinition<T> = {
  type: 'boolean' | 'string' | 'number'
  defaultValue: T
  description: string
} & (EvenSplitDefinition<T> | WeightedSplitDefinition<T>)

export type FeaturePrimitive = boolean | string | number

export interface FeaturesDefinition {
  [key: string]: FeatureDefinition<FeaturePrimitive>
}

/**
 * INTERNAL SOP DOCS FOR SETTING UP A FLAG AND SPLIT TEST: https://redbrickmedia.atlassian.net/wiki/spaces/Duplex/pages/3686957075/ConfigCat+Testing+SOP
 */
export const flags: FeaturesDefinition = {
  splittestname: {
    type: 'string',
    defaultValue: 'control',
    description:
      'The name of the A/B test split to use. It is the parent flag that child flag/configs will look to for evaluation.',
    values: ['control', 'DXT-####_variant_a'],
  },
  layoutSkeleton: {
    type: 'string',
    defaultValue: 'control',
    description: 'The layout skeleton to use',
    values: ['control', '{splittestname}'],
  },
  siteSettings: {
    type: 'string',
    defaultValue: 'control',
    description: 'Which version of site settings to use',
    values: ['control', '{splittestname}'],
  },
  layoutSettings: {
    type: 'string',
    defaultValue: 'control',
    description: 'Which version of layout settings to use',
    values: ['control', '{splittestname}'],
  },
  adSettings: {
    type: 'string',
    defaultValue: 'control',
    description: 'Which version of layout settings to use',
    values: ['control', '{splittestname}'],
  },
  summaryPageSettings: {
    type: 'string',
    defaultValue: 'control',
    description: 'Which version of summary page settings to use',
    values: ['control', '{splittestname}'],
  },
  contentSource: {
    type: 'string',
    defaultValue: 'content-api-v1',
    description: 'Which content source to use',
    values: ['content-api', 'content-api-v1', 'discover-feed', 'dplxapi'],
  },
  showArticleImage: {
    type: 'boolean',
    defaultValue: false,
    description: 'Whether to show the article image',
    values: [true, false],
  },
  showCategoryInsteadOfSource: {
    type: 'boolean',
    defaultValue: false,
    description: 'DXT-1403: show category instead of source on homepage',
    values: [true, false],
  },
  discoverTab: {
    type: 'boolean',
    defaultValue: false,
    description: 'DXT-1453: Add Discover tab as the default homepage tab.',
    values: [true, false],
  },
  tabBadges: {
    type: 'string',
    defaultValue: '',
    description:
      'JSON configuration for tab badges. Format: [{"tab":"TabName","variant":"pill|count","label":"Text","tone":"blue|red"}]',
    values: [''],
  },
  quartzPromoDropdown: {
    type: 'string',
    defaultValue: 'off',
    description:
      'DXT-2018: Configuration for Quartz promo dropdown (off, what_happened_while_you_slept, we_couldve_just_emailed_you, become_informed_in_5_minutes)',
    values: [
      'off',
      'what_happened_while_you_slept',
      'we_couldve_just_emailed_you',
      'become_informed_in_5_minutes',
    ],
  },
  quartzPromoCooldown: {
    type: 'number',
    defaultValue: 3,
    description:
      'DXT-2018: Cooldown period in days before showing Quartz promo again after dismissal (0 = show every time)',
    values: [0, 3],
  },
  nativeAdProvider: {
    defaultValue: 'outbrain',
    description: 'Which native ad provider to use',
    type: 'string',
    values: ['outbrain', 'taboola', 'dplxapi'],
  },
  enableMixedArticleFeed: {
    type: 'boolean',
    defaultValue: false,
    description:
      'Enabling/disabling mixed article feed. Enabling it would allow fetching multiple article types from content api',
    values: [true, false],
  },
  rewardedAd: {
    type: 'boolean',
    defaultValue: false,
    description:
      'Enable GPT rewarded ad on article clicks, read by AY via window.openfeature',
    values: [true, false],
  },
  localNewsEnabled: {
    type: 'boolean',
    defaultValue: false,
    description:
      'Enables local news experiment. When true, users see news from local publishers based on their detected city. Currently only supports Houston.',
    values: [true, false],
  },
}
